// WorkflowKanbanBoard.js
// Full kanban board showing workflow instances organized by pipeline stage.
// Uses HTML5 drag-and-drop (no external dnd library required).

import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  LinearProgress,
  CircularProgress,
  Alert,
  Avatar,
  Tooltip,
  IconButton,
  TextField,
  MenuItem,
  Badge,
  Skeleton,
  Button,
  Select,
  FormControl,
  InputLabel,
  Snackbar,
} from '@mui/material';
import {
  Add,
  Warning,
  CheckCircle,
  AccessTime,
  DragIndicator,
  Person,
  Refresh,
  Receipt,
} from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import api from '../api';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const STAGES = [
  { key: 'new',            label: 'New',            color: '#9e9e9e' },
  { key: 'gathering_info', label: 'Gathering Info',  color: '#2196f3' },
  { key: 'in_preparation', label: 'In Preparation',  color: '#ff9800' },
  { key: 'in_review',      label: 'In Review',       color: '#9c27b0' },
  { key: 'completed',      label: 'Completed',       color: '#4caf50' },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const isOverdue = (dueDateStr) => {
  if (!dueDateStr) return false;
  // Parse as UTC if no timezone info, compare to current UTC time
  const iso = dueDateStr.includes('T') ? dueDateStr : `${dueDateStr}T23:59:59Z`;
  return new Date(iso) < new Date();
};

const formatDate = (dateStr) => {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
};

const initials = (name = '') =>
  name
    .split(' ')
    .map((n) => n[0])
    .join('')
    .toUpperCase()
    .slice(0, 2);

// ---------------------------------------------------------------------------
// WorkflowCard
// ---------------------------------------------------------------------------

const WorkflowCard = ({ instance, onDragStart, onClick, stagesConfig }) => {
  const overdue = isOverdue(instance.due_date);
  const stageObj = (stagesConfig || STAGES).find((s) => s.key === instance.pipeline_stage) || STAGES[0];

  return (
    <Paper
      draggable
      onDragStart={(e) => onDragStart(e, instance)}
      onClick={onClick}
      elevation={1}
      sx={{
        p: 1.5,
        mb: 1,
        cursor: 'pointer',
        borderLeft: instance.needs_attention ? `4px solid #ff9800` : `4px solid ${stageObj.color}`,
        transition: 'box-shadow 0.15s, transform 0.1s',
        '&:hover': { boxShadow: 4, transform: 'translateY(-1px)' },
        '&:active': { cursor: 'grabbing' },
        bgcolor: 'background.paper',
        borderRadius: 1.5,
        position: 'relative',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      {/* Header row: client name + attention badge */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 0.5 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}>
          <DragIndicator sx={{ fontSize: 14, color: 'text.disabled', flexShrink: 0 }} />
          <Tooltip title={instance.client_name || 'Unknown Client'}>
            <Typography variant="subtitle2" fontWeight={700} noWrap sx={{ maxWidth: 150 }}>
              {instance.client_name || 'Unknown Client'}
            </Typography>
          </Tooltip>
        </Box>
        {instance.needs_attention && (
          <Tooltip title={instance.attention_reason || 'Needs attention'}>
            <Warning sx={{ fontSize: 16, color: 'warning.main', flexShrink: 0 }} />
          </Tooltip>
        )}
      </Box>

      {/* Template name */}
      <Tooltip title={instance.template_name || 'No template'}>
        <Typography variant="caption" color="text.secondary" noWrap display="block" sx={{ mb: 0.75 }}>
          {instance.template_name || 'No template'}
        </Typography>
      </Tooltip>

      {/* Organizer (tax year) */}
      {instance.organizer_name && (
        <Typography variant="caption" color="primary.main" noWrap display="block" sx={{ mb: 0.5, fontWeight: 500 }}>
          {instance.organizer_name}
        </Typography>
      )}

      {/* Progress bar */}
      <Box sx={{ mb: 0.75 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.25 }}>
          <Typography variant="caption" color="text.secondary">
            {instance.tasks_completed ?? 0}/{instance.tasks_total ?? 0} tasks
          </Typography>
          <Typography variant="caption" color="text.secondary">
            {instance.progress_percentage ?? 0}%
          </Typography>
        </Box>
        <LinearProgress
          variant="determinate"
          value={instance.progress_percentage ?? 0}
          sx={{
            height: 5,
            borderRadius: 3,
            bgcolor: 'grey.200',
            '& .MuiLinearProgress-bar': { bgcolor: stageObj.color },
          }}
        />
      </Box>

      {/* Due date + completed icon */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
          <AccessTime sx={{ fontSize: 13, color: overdue ? 'error.main' : 'text.disabled' }} />
          <Typography
            variant="caption"
            color={overdue ? 'error.main' : 'text.secondary'}
            fontWeight={overdue ? 600 : 400}
          >
            {formatDate(instance.due_date)}
            {overdue ? ' (Overdue)' : ''}
          </Typography>
        </Box>
        {instance.progress_percentage === 100 && (
          <Tooltip title="Completed">
            <CheckCircle sx={{ fontSize: 15, color: 'success.main' }} />
          </Tooltip>
        )}
      </Box>

      {/* Invoice + assignee row */}
      <Box sx={{ mt: 0.75, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        {instance.invoice_outstanding > 0 && (
          <Tooltip title={`${instance.invoice_outstanding} outstanding invoice(s)`}>
            <Chip
              icon={<Receipt sx={{ fontSize: 11 }} />}
              label={`${instance.invoice_outstanding} due`}
              size="small"
              color="warning"
              variant="outlined"
              sx={{ height: 18, fontSize: '0.65rem' }}
            />
          </Tooltip>
        )}
        {instance.invoice_outstanding === 0 && instance.invoice_paid > 0 && (
          <Chip
            label="Paid"
            size="small"
            color="success"
            variant="outlined"
            sx={{ height: 18, fontSize: '0.65rem' }}
          />
        )}
        <Box sx={{ flex: 1 }} />
        {instance.assigned_to_name && (
          <Tooltip title={instance.assigned_to_name}>
            <Avatar sx={{ width: 22, height: 22, fontSize: 10, bgcolor: stageObj.color }}>
              {initials(instance.assigned_to_name)}
            </Avatar>
          </Tooltip>
        )}
      </Box>
    </Paper>
  );
};

// ---------------------------------------------------------------------------
// ColumnSkeleton
// ---------------------------------------------------------------------------

const ColumnSkeleton = () => (
  <Box sx={{ flex: '0 0 220px', minWidth: 220 }}>
    <Skeleton variant="rectangular" height={36} sx={{ mb: 1, borderRadius: 1 }} />
    {[1, 2, 3].map((i) => (
      <Skeleton key={i} variant="rectangular" height={110} sx={{ mb: 1, borderRadius: 1 }} />
    ))}
  </Box>
);

// ---------------------------------------------------------------------------
// Main Component
// ---------------------------------------------------------------------------

const WorkflowKanbanBoard = ({ onWorkflowClick }) => {
  const navigate = useNavigate();
  const draggedRef = useRef(null);

  const [stages, setStages] = useState(STAGES); // dynamic or fallback to hardcoded
  const [stageMap, setStageMap] = useState(
    Object.fromEntries(STAGES.map((s) => [s.key, []]))
  );
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [dragOverStage, setDragOverStage] = useState(null);
  const [movingId, setMovingId] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  // Filter state
  const [allClients, setAllClients] = useState([]);
  const [allTemplates, setAllTemplates] = useState([]);
  const [filterClient, setFilterClient] = useState('');
  const [filterTemplate, setFilterTemplate] = useState('');
  const [filterOverdue, setFilterOverdue] = useState(false);
  const [searchText, setSearchText] = useState('');

  // -------------------------------------------------------------------------
  // Data fetching
  // -------------------------------------------------------------------------

  const fetchPipeline = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Load dynamic pipeline stages from DB (with fallback to hardcoded)
      let activeStages = STAGES;
      try {
        const stagesRes = await api.get('/api/multi-tenant/pipeline-stages/');
        const dbStages = stagesRes.data?.stages ?? [];
        if (dbStages.length > 0) {
          activeStages = dbStages
            .sort((a, b) => a.order - b.order)
            .map((s) => ({
              key: s.maps_to_legacy || s.name.toLowerCase().replace(/\s+/g, '_'),
              label: s.name,
              color: s.color || '#9e9e9e',
            }));
          setStages(activeStages);
        }
      } catch {
        // fallback to hardcoded STAGES — no action needed
      }

      const res = await api.get('/api/multi-tenant/workflow-pipeline/');
      const rawStages = res.data?.stages ?? res.data ?? [];

      // Build stageMap from API response
      const map = Object.fromEntries(activeStages.map((s) => [s.key, []]));

      if (Array.isArray(rawStages)) {
        rawStages.forEach((stageObj) => {
          const key = stageObj.id ?? stageObj.key ?? stageObj.stage;
          if (key !== undefined) {
            if (map[key] === undefined) map[key] = [];
            map[key] = Array.isArray(stageObj.workflows) ? stageObj.workflows : [];
          }
        });
      } else if (typeof rawStages === 'object') {
        Object.keys(rawStages).forEach((key) => {
          if (map[key] !== undefined) {
            map[key] = Array.isArray(rawStages[key]) ? rawStages[key] : [];
          }
        });
      }

      setStageMap(map);

      // Derive client/template lists for filter dropdowns
      const allWorkflows = Object.values(map).flat();
      const clients = [...new Set(allWorkflows.map((w) => w.client_name).filter(Boolean))];
      const templates = [...new Set(allWorkflows.map((w) => w.template_name).filter(Boolean))];
      setAllClients(clients);
      setAllTemplates(templates);
    } catch (err) {
      setError(err.response?.data?.error || err.message || 'Failed to load pipeline data.');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  // -------------------------------------------------------------------------
  // Drag-and-drop handlers (HTML5)
  // -------------------------------------------------------------------------

  const handleDragStart = (e, instance) => {
    draggedRef.current = instance;
    e.dataTransfer.effectAllowed = 'move';
    e.dataTransfer.setData('text/plain', String(instance.id));
  };

  const handleDragOver = (e, stageKey) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    setDragOverStage(stageKey);
  };

  const handleDragLeave = () => {
    setDragOverStage(null);
  };

  const handleDrop = async (e, targetStageKey) => {
    e.preventDefault();
    setDragOverStage(null);

    const instance = draggedRef.current;
    draggedRef.current = null;

    if (!instance || instance.pipeline_stage === targetStageKey) return;

    const sourceStage = instance.pipeline_stage;
    const workflowId = instance.id;
    const targetLabel = stages.find((s) => s.key === targetStageKey)?.label || targetStageKey;

    // Optimistic UI update
    setStageMap((prev) => {
      const next = { ...prev };
      next[sourceStage] = (prev[sourceStage] || []).filter((w) => w.id !== workflowId);
      next[targetStageKey] = [...(prev[targetStageKey] || []), { ...instance, pipeline_stage: targetStageKey }];
      return next;
    });

    setMovingId(workflowId);
    try {
      await api.patch(`/api/multi-tenant/workflows/${workflowId}/move-stage/`, {
        pipeline_stage: targetStageKey,
      });
      setSnackbar({ open: true, message: `Moved to "${targetLabel}"` });
    } catch (err) {
      // Revert on failure
      setStageMap((prev) => {
        const next = { ...prev };
        next[targetStageKey] = (prev[targetStageKey] || []).filter((w) => w.id !== workflowId);
        next[sourceStage] = [...(prev[sourceStage] || []), { ...instance, pipeline_stage: sourceStage }];
        return next;
      });
      setError('Failed to move workflow. Please try again.');
    } finally {
      setMovingId(null);
    }
  };

  // -------------------------------------------------------------------------
  // Filtering
  // -------------------------------------------------------------------------

  const filterWorkflows = (workflows) =>
    workflows.filter((w) => {
      if (filterClient && w.client_name !== filterClient) return false;
      if (filterTemplate && w.template_name !== filterTemplate) return false;
      if (filterOverdue && !isOverdue(w.due_date)) return false;
      if (searchText) {
        const q = searchText.toLowerCase();
        const clientMatch = (w.client_name || '').toLowerCase().includes(q);
        const templateMatch = (w.template_name || '').toLowerCase().includes(q);
        const organizerMatch = (w.organizer_name || '').toLowerCase().includes(q);
        if (!clientMatch && !templateMatch && !organizerMatch) return false;
      }
      return true;
    });

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 2 }}>
      {/* Top bar */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
          mb: 2,
        }}
      >
        {/* Client filter */}
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>All Clients</InputLabel>
          <Select
            value={filterClient}
            label="All Clients"
            onChange={(e) => setFilterClient(e.target.value)}
          >
            <MenuItem value="">All Clients</MenuItem>
            {allClients.map((c) => (
              <MenuItem key={c} value={c}>
                {c}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Template filter */}
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>All Templates</InputLabel>
          <Select
            value={filterTemplate}
            label="All Templates"
            onChange={(e) => setFilterTemplate(e.target.value)}
          >
            <MenuItem value="">All Templates</MenuItem>
            {allTemplates.map((t) => (
              <MenuItem key={t} value={t}>
                {t}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        {/* Search */}
        <TextField
          size="small"
          placeholder="Search..."
          value={searchText}
          onChange={(e) => setSearchText(e.target.value)}
          sx={{ minWidth: 200 }}
        />

        {/* Overdue filter chip */}
        <Chip
          label="Overdue only"
          size="small"
          color={filterOverdue ? 'error' : 'default'}
          variant={filterOverdue ? 'filled' : 'outlined'}
          onClick={() => setFilterOverdue((v) => !v)}
          sx={{ cursor: 'pointer' }}
        />

        {(filterClient || filterTemplate || filterOverdue || searchText) && (
          <Button
            size="small"
            onClick={() => { setFilterClient(''); setFilterTemplate(''); setFilterOverdue(false); setSearchText(''); }}
            sx={{ textTransform: 'none', color: 'text.secondary' }}
          >
            Clear filters
          </Button>
        )}

        <Box sx={{ flexGrow: 1 }} />

        <Tooltip title="Refresh">
          <IconButton onClick={fetchPipeline} disabled={loading}>
            <Refresh />
          </IconButton>
        </Tooltip>

        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => navigate('/workflows')}
          size="small"
        >
          Assign Workflow
        </Button>
      </Box>

      {/* Error */}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 1 }}>
          {error}
        </Alert>
      )}

      {/* Board */}
      <Box
        sx={{
          display: 'flex',
          gap: 2,
          overflowX: 'auto',
          flex: 1,
          pb: 1,
          alignItems: 'flex-start',
        }}
      >
        {loading
          ? STAGES.map((s) => <ColumnSkeleton key={s.key} />)
          : stages.map((stage) => {
              const workflows = filterWorkflows(stageMap[stage.key] || []);
              const overdueCount = workflows.filter((w) => isOverdue(w.due_date)).length;
              const isDragTarget = dragOverStage === stage.key;

              return (
                <Box
                  key={stage.key}
                  sx={{ flex: '0 0 240px', minWidth: 240, display: 'flex', flexDirection: 'column' }}
                  onDragOver={(e) => handleDragOver(e, stage.key)}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, stage.key)}
                >
                  {/* Column header */}
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      mb: 1,
                      px: 1,
                      py: 0.75,
                      borderRadius: 1.5,
                      bgcolor: `${stage.color}14`,
                      borderBottom: `3px solid ${stage.color}`,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
                      <Box
                        sx={{
                          width: 10,
                          height: 10,
                          borderRadius: '50%',
                          bgcolor: stage.color,
                          flexShrink: 0,
                        }}
                      />
                      <Box>
                        <Typography variant="subtitle2" fontWeight={700} lineHeight={1.2}>
                          {stage.label}
                        </Typography>
                        {overdueCount > 0 && (
                          <Typography variant="caption" sx={{ color: 'error.main', fontWeight: 600 }}>
                            {overdueCount} overdue
                          </Typography>
                        )}
                      </Box>
                    </Box>
                    <Chip
                      label={workflows.length}
                      size="small"
                      sx={{
                        height: 20,
                        fontSize: 11,
                        bgcolor: stage.color,
                        color: '#fff',
                        fontWeight: 700,
                      }}
                    />
                  </Box>

                  {/* Drop zone */}
                  <Box
                    sx={{
                      flex: 1,
                      minHeight: 120,
                      p: 0.5,
                      borderRadius: 1.5,
                      border: isDragTarget
                        ? `2px dashed ${stage.color}`
                        : '2px dashed transparent',
                      bgcolor: isDragTarget ? `${stage.color}14` : 'transparent',
                      transition: 'border 0.15s, background-color 0.15s',
                    }}
                  >
                    {workflows.length === 0 ? (
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          height: 100,
                          borderRadius: 1.5,
                          border: '1px dashed',
                          borderColor: 'divider',
                          gap: 0.5,
                        }}
                      >
                        <Typography variant="caption" color="text.disabled" sx={{ textAlign: 'center' }}>
                          No workflows here
                        </Typography>
                        <Typography variant="caption" color="text.disabled" sx={{ fontSize: '0.65rem' }}>
                          Drag a card to this column
                        </Typography>
                      </Box>
                    ) : (
                      workflows.map((instance) => (
                        <Box key={instance.id} sx={{ position: 'relative' }}>
                          {movingId === instance.id && (
                            <CircularProgress
                              size={14}
                              sx={{ position: 'absolute', top: 8, right: 8, zIndex: 1 }}
                            />
                          )}
                          <WorkflowCard
                            instance={instance}
                            onDragStart={handleDragStart}
                            stagesConfig={stages}
                            onClick={() => onWorkflowClick ? onWorkflowClick(instance) : navigate(`/clients/${instance.client_id}`)}
                          />
                        </Box>
                      ))
                    )}
                  </Box>
                </Box>
              );
            })}
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        message={snackbar.message}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </Box>
  );
};

export default WorkflowKanbanBoard;
